/**
 * Page Designer table content.
 *
 * CARD is the layout container. TABLE is content inside the card.
 * This module is a read-only registry over existing EMRbyMD APIs/tables.
 * It does not create database tables or a query builder.
 */

import { fetchPatients, patientNameOf } from "@/lib/encounterApi";
import { formatPmhDiagnosis } from "@/lib/pmhCatalog";
import { listMedicalHistory } from "@/lib/pmhApi";
import { listSurgicalHistory } from "@/lib/surgicalHistoryApi";

export const CONTENT_TYPE_TABLE = "table";
export const TABLE_PREFERRED_WIDTH = 12;
export const TABLE_PREFERRED_HEIGHT = 8;
export const TABLE_MAX_ROWS_DEFAULT = 10;
export const TABLE_MAX_ROWS_LIMIT = 50;
export const TABLE_SORT_ASC = "asc";
export const TABLE_SORT_DESC = "desc";

export const TABLE_SOURCE_MEDICAL_HISTORY = "patient-medical-history";
export const TABLE_SOURCE_SURGICAL_HISTORY = "patient-surgical-history";

function text(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.display || value.label || value.code || "";
  }
  return String(value);
}

function lateralityValue(item) {
  return text(item?.laterality) || text(item?.lateralityCode);
}

export const TABLE_SOURCES = Object.freeze([
  Object.freeze({
    id: TABLE_SOURCE_MEDICAL_HISTORY,
    label: "Medical History",
    table: "patient_medical_history",
    patientScoped: true,
    columns: Object.freeze([
      Object.freeze({ id: "diagnosis", label: "Diagnosis" }),
      Object.freeze({ id: "code", label: "Code" }),
      Object.freeze({ id: "onsetDate", label: "Date" }),
      Object.freeze({ id: "status", label: "Status" }),
      Object.freeze({ id: "comment", label: "Notes" }),
      Object.freeze({ id: "source", label: "Source" }),
      Object.freeze({ id: "kind", label: "Kind" }),
    ]),
    cellValue(item, columnId) {
      if (columnId === "diagnosis") return formatPmhDiagnosis(item);
      if (columnId === "code") return text(item?.code);
      if (columnId === "onsetDate") return text(item?.onsetDate);
      if (columnId === "status") return text(item?.status);
      if (columnId === "comment") return text(item?.comment);
      if (columnId === "source") return text(item?.source);
      if (columnId === "kind") return text(item?.kind);
      return "";
    },
    async fetchRows(patientId) {
      const data = await listMedicalHistory(patientId);
      return Array.isArray(data?.items) ? data.items : [];
    },
  }),
  Object.freeze({
    id: TABLE_SOURCE_SURGICAL_HISTORY,
    label: "Surgical History",
    table: "patient_surgical_history",
    patientScoped: true,
    columns: Object.freeze([
      Object.freeze({ id: "procedure", label: "Procedure" }),
      Object.freeze({ id: "performedDate", label: "Date" }),
      Object.freeze({ id: "status", label: "Status" }),
      Object.freeze({ id: "laterality", label: "Laterality" }),
      Object.freeze({ id: "comment", label: "Notes" }),
    ]),
    cellValue(item, columnId) {
      if (columnId === "procedure") {
        return text(item?.display) || text(item?.shortDisplay) || text(item?.localCode);
      }
      if (columnId === "performedDate") return text(item?.performedDate);
      if (columnId === "status") return text(item?.status);
      if (columnId === "laterality") return lateralityValue(item);
      if (columnId === "comment") return text(item?.comment);
      return "";
    },
    async fetchRows(patientId) {
      const data = await listSurgicalHistory(patientId);
      return Array.isArray(data?.items) ? data.items : [];
    },
  }),
]);

export function emptyTableContent() {
  return {
    sourceId: "",
    columns: [],
    title: "",
    sortColumn: "",
    sortDirection: TABLE_SORT_ASC,
    maxRows: TABLE_MAX_ROWS_DEFAULT,
    previewPatientId: "",
  };
}

export function tableSource(sourceId) {
  const id = typeof sourceId === "string" ? sourceId.trim() : "";
  return TABLE_SOURCES.find((source) => source.id === id) || null;
}

export function tableColumns(sourceId) {
  return tableSource(sourceId)?.columns || [];
}

export function isTableCard(card) {
  return (card?.contentType || "") === CONTENT_TYPE_TABLE;
}

function asInt(value, fallback) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function sanitizeTitle(value) {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, 120);
}

function sanitizePatientId(value) {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, 80);
}

export function sanitizeTableContent(raw) {
  const empty = emptyTableContent();
  if (!raw || typeof raw !== "object") return empty;
  const source = tableSource(raw.sourceId);
  const sourceId = source ? source.id : "";
  const allowed = new Set((source?.columns || []).map((column) => column.id));
  const columns = [];
  const seen = new Set();
  (Array.isArray(raw.columns) ? raw.columns : []).forEach((value) => {
    const id = typeof value === "string" ? value.trim() : "";
    if (!id || seen.has(id) || (sourceId && !allowed.has(id))) return;
    seen.add(id);
    columns.push(id);
  });
  const sortColumn = columns.includes(raw.sortColumn) ? raw.sortColumn : "";
  const sortDirection = raw.sortDirection === TABLE_SORT_DESC ? TABLE_SORT_DESC : TABLE_SORT_ASC;
  const maxRows = Math.min(
    TABLE_MAX_ROWS_LIMIT,
    Math.max(1, asInt(raw.maxRows, TABLE_MAX_ROWS_DEFAULT)),
  );
  return {
    sourceId,
    columns,
    title: sanitizeTitle(raw.title),
    sortColumn,
    sortDirection,
    maxRows,
    previewPatientId: sanitizePatientId(raw.previewPatientId),
  };
}

export function tableContentEquals(left, right) {
  const a = sanitizeTableContent(left);
  const b = sanitizeTableContent(right);
  return (
    a.sourceId === b.sourceId &&
    a.title === b.title &&
    a.sortColumn === b.sortColumn &&
    a.sortDirection === b.sortDirection &&
    a.maxRows === b.maxRows &&
    a.previewPatientId === b.previewPatientId &&
    a.columns.length === b.columns.length &&
    a.columns.every((id, index) => id === b.columns[index])
  );
}

export function addTableColumn(content, columnId) {
  const next = sanitizeTableContent(content);
  const source = tableSource(next.sourceId);
  if (!source || !source.columns.some((column) => column.id === columnId)) return next;
  if (next.columns.includes(columnId)) return next;
  return sanitizeTableContent({ ...next, columns: [...next.columns, columnId] });
}

export function removeTableColumn(content, columnId) {
  const next = sanitizeTableContent(content);
  return sanitizeTableContent({
    ...next,
    columns: next.columns.filter((id) => id !== columnId),
  });
}

export function moveTableColumn(content, columnId, direction) {
  const next = sanitizeTableContent(content);
  const index = next.columns.indexOf(columnId);
  if (index < 0) return next;
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= next.columns.length) return next;
  const columns = [...next.columns];
  const [moved] = columns.splice(index, 1);
  columns.splice(target, 0, moved);
  return sanitizeTableContent({ ...next, columns });
}

export function availableTableColumns(content) {
  const next = sanitizeTableContent(content);
  const selected = new Set(next.columns);
  return tableColumns(next.sourceId).filter((column) => !selected.has(column.id));
}

export function selectedTableColumns(content) {
  const next = sanitizeTableContent(content);
  const byId = new Map(tableColumns(next.sourceId).map((column) => [column.id, column]));
  return next.columns.map((id) => byId.get(id)).filter(Boolean);
}

export function tableCellValue(sourceId, item, columnId) {
  const source = tableSource(sourceId);
  if (!source) return "";
  return source.cellValue(item, columnId) || "";
}

export function sortTableRows(rows, content) {
  const config = sanitizeTableContent(content);
  const list = Array.isArray(rows) ? [...rows] : [];
  if (!config.sortColumn) return list;
  const direction = config.sortDirection === TABLE_SORT_DESC ? -1 : 1;
  list.sort((left, right) => {
    const a = tableCellValue(config.sourceId, left, config.sortColumn).toLowerCase();
    const b = tableCellValue(config.sourceId, right, config.sortColumn).toLowerCase();
    if (a < b) return -1 * direction;
    if (a > b) return 1 * direction;
    return 0;
  });
  return list;
}

export function presentTableRows(rows, content) {
  const config = sanitizeTableContent(content);
  return sortTableRows(rows, config).slice(0, config.maxRows);
}

export async function loadTableRows(content) {
  const config = sanitizeTableContent(content);
  const source = tableSource(config.sourceId);
  if (!source) {
    return { rows: [], status: "idle" };
  }
  if (source.patientScoped && !config.previewPatientId) {
    return { rows: [], status: "need-patient" };
  }
  try {
    const rows = await source.fetchRows(config.previewPatientId);
    return { rows: presentTableRows(rows, config), status: "ready" };
  } catch {
    return { rows: [], status: "error" };
  }
}

export async function loadTablePreviewPatients() {
  try {
    const items = await fetchPatients();
    return (Array.isArray(items) ? items : []).map((patient) => ({
      id: patient.id,
      label: patientNameOf(patient) || patient.id,
    }));
  } catch {
    return [];
  }
}

export function defaultColumnsForSource(sourceId) {
  if (sourceId === TABLE_SOURCE_MEDICAL_HISTORY) return ["diagnosis", "onsetDate", "status"];
  if (sourceId === TABLE_SOURCE_SURGICAL_HISTORY) return ["procedure", "performedDate", "status"];
  return [];
}

export function defaultTableTitle(sourceId) {
  if (sourceId === TABLE_SOURCE_MEDICAL_HISTORY) return "Past Medical History";
  if (sourceId === TABLE_SOURCE_SURGICAL_HISTORY) return "Past Surgical History";
  return tableSource(sourceId)?.label || "";
}
